import { Text } from '@chakra-ui/react'
import { DateTime } from 'luxon'
import { useState } from 'react'

import { isNotNull } from '@/utils/helpers'

import SeriesQ from '@/queries/SeriesQ'
import SeriesRegistrationQ from '@/queries/SeriesRegistrationQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { SQL } from '@/client/query'
import { RPCRefresh } from '@/client/rpc'
import { SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS } from '@/client/services/satBootcamp/constants'
import { getPersonalizedSurveyUrl } from '@/client/services/url'
import { SAT_BOOTCAMP_WORLD_SLUG } from '@/client/services/worlds/constants'

import buildSurveyTodoItems, {
  type SurveyTodoConfig,
} from '@/components/Worlds/Todos/hooks/buildSurveyTodoItems'
import type { TodoItem } from '@/components/Worlds/Todos/types'

const SAT_BOOTCAMP_LEARNER_PRE_SERIES_SURVEY_URL = 'https://form.typeform.com/to/R17Wy6WZ'
const SAT_BOOTCAMP_LEARNER_POST_SERIES_SURVEY_URL = 'https://form.typeform.com/to/t4jpheka'

const LEARNER_PRE_SURVEY_ESTIMATED_TIME = '5'
const LEARNER_POST_SURVEY_ESTIMATED_TIME = '5-10'
const SHOW_POST_SERIES_SURVEY_DAYS_BEFORE_END = 5
const POST_SERIES_SURVEY_DEADLINE_DAYS_AFTER_END = 7
const DAYS_TO_INCLUDE_SERIES_AFTER_END = 7

type LearnerSeriesRegistrationDetails = {
  seriesId: number
  startDate: DateTime
  endDate: DateTime
  completedPreSeriesSurvey: boolean
  completedPostSeriesSurvey: boolean
}

function getSurveyTodosForRegistration({
  registration,
  uuidTraining,
}: {
  registration: LearnerSeriesRegistrationDetails
  uuidTraining: string
}): SurveyTodoConfig[] {
  const showPostSeriesSurveyAfter = registration.endDate.minus({
    days: SHOW_POST_SERIES_SURVEY_DAYS_BEFORE_END,
  })
  const showPostSeriesSurvey = DateTime.now() > showPostSeriesSurveyAfter

  const postSeriesSurveyDeadline = registration.endDate.plus({
    days: POST_SERIES_SURVEY_DEADLINE_DAYS_AFTER_END,
  })

  const preSurveyUrl = SAT_BOOTCAMP_LEARNER_PRE_SERIES_SURVEY_URL
  const preSurveyTodo: SurveyTodoConfig | null =
    preSurveyUrl != null
      ? {
          title: 'Complete the SAT Bootcamp Pre-Survey',
          subtitle: 'To help us improve the program!',
          deadline: registration.startDate,
          completed: registration.completedPreSeriesSurvey,
          modalDetails: {
            key: `sat-bootcamp-series-${registration.seriesId}-learner-pre-survey`,
            title: 'SAT Bootcamp Pre-Survey',
            body: () => (
              <Text>
                Fill out this {LEARNER_PRE_SURVEY_ESTIMATED_TIME} minute pre-survey for the SAT
                Bootcamp as part of the College Board research study.
              </Text>
            ),
            link: getPersonalizedSurveyUrl({
              surveyUrl: preSurveyUrl,
              uuidTraining,
              worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
            }),
            onMarkComplete: () =>
              RPCRefresh.markSeriesSurveyComplete({
                seriesId: registration.seriesId,
                surveyType: 'learner-pre-survey',
              }),
          },
        }
      : null

  const postSurveyUrl = SAT_BOOTCAMP_LEARNER_POST_SERIES_SURVEY_URL
  const postSurveyTodo: SurveyTodoConfig | null =
    postSurveyUrl != null && showPostSeriesSurvey
      ? {
          title: 'Complete the SAT Bootcamp Post-Survey',
          subtitle: 'To help us improve the program!',
          deadline: postSeriesSurveyDeadline,
          completed: registration.completedPostSeriesSurvey,
          modalDetails: {
            key: `sat-bootcamp-series-${registration.seriesId}-learner-post-survey`,
            title: 'SAT Bootcamp Post-Survey',
            body: () => (
              <Text>
                Fill out this {LEARNER_POST_SURVEY_ESTIMATED_TIME} minute post-survey for the SAT
                Bootcamp as part of the College Board research study.
              </Text>
            ),
            link: getPersonalizedSurveyUrl({
              surveyUrl: postSurveyUrl,
              uuidTraining,
              worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
            }),
            onMarkComplete: () =>
              RPCRefresh.markSeriesSurveyComplete({
                seriesId: registration.seriesId,
                surveyType: 'learner-post-survey',
              }),
          },
        }
      : null

  return [preSurveyTodo, postSurveyTodo].filter(isNotNull)
}

export default function useSATBootcampLearnerSurveyTodos(): TodoItem[] {
  const user = useUser()
  const query = useQuery()
  const [openModalKey, setOpenModalKey] = useState<string | null>(null)

  const registeredSeriesDetails: LearnerSeriesRegistrationDetails[] = query
    .many(
      SeriesRegistrationQ.where({
        userId: user.id,
        include: [
          {
            association: 'Series',
            where: SQL`
              ${SeriesQ.isActiveOrEndedWithinDays(DAYS_TO_INCLUDE_SERIES_AFTER_END)}
              AND ${SeriesQ.hasAnyTemplateSlug(SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS)}
            `,
            required: true,
          },
        ],
      })
    )
    .map(registration => ({
      seriesId: registration(SeriesQ.id) ?? 0,
      startDate: DateTime.fromISO(`${registration(SeriesQ.getComputedStartDate())}`),
      endDate: DateTime.fromISO(`${registration(SeriesQ.getComputedEndDate())}`),
      completedPreSeriesSurvey:
        registration(SeriesRegistrationQ.hasCompletedPreSeriesSurvey()) ?? false,
      completedPostSeriesSurvey:
        registration(SeriesRegistrationQ.hasCompletedPostSeriesSurvey()) ?? false,
    }))

  const surveyTodoConfigs = registeredSeriesDetails.flatMap(registration =>
    getSurveyTodosForRegistration({
      registration,
      uuidTraining: user.uuidTraining,
    })
  )

  const allTodos = buildSurveyTodoItems({
    configs: surveyTodoConfigs,
    worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
    targetLevel: 'member',
    openModalKey,
    setOpenModalKey,
  })

  return allTodos.filter(todo => todo.deadline == null || DateTime.now() < todo.deadline)
}
